import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, Eye, Pencil, Sparkles, UserRoundCog } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { SupportMode, SupportSession, useSupportSessionsApi } from '../../common/hooks/use-support-sessions-api.hook';
import { useVendorsApi } from '../../common/hooks/use-vendors-api.hook';
import { PillTabs } from '../../components/design-system/admin';
import { VendorCombobox } from '../superadmin/edit-user/components/vendor-combobox';
import { VENDOR_PICKER_LIMIT, VENDOR_PICKER_PAGE } from '../superadmin/edit-user/components/user-general-information.form';

type VendorKind = 'customer' | 'prospect';

/**
 * Act-as: a super admin works in a vendor's account as Ardealy support, view
 * only or can edit, for 60 minutes. Without a vendor (the dashboard's quick
 * action), one is picked here first, from the customers or the prospects.
 */
export function OpenAsVendorDialog({ vendor: given, open, onOpenChange, onStarted }: {
    vendor?: { _id: string; name?: string };
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The session is open: show it (in a SupportSessionFrame). */
    onStarted: (session: SupportSession) => void;
}) {
    const { t } = useTranslation();
    const api = useSupportSessionsApi();
    const vendorsApi = useVendorsApi();
    const [mode, setMode] = useState<SupportMode>('view');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [kind, setKind] = useState<VendorKind>('customer');
    // Each tab's list, loaded the first time that tab is shown.
    const [lists, setLists] = useState<Partial<Record<VendorKind, any[]>>>({});
    const [picked, setPicked] = useState<{ _id: string; name?: string } | null>(null);
    const vendor = given ?? picked;
    const vendors = lists[kind];

    useEffect(() => {
        if (!open || given || vendors) return;
        let cancelled = false;
        vendorsApi
            .findAllVendors(VENDOR_PICKER_PAGE, VENDOR_PICKER_LIMIT, undefined, kind)
            .then((res: any) => !cancelled && setLists((all) => ({ ...all, [kind]: res?.data ?? [] })))
            .catch(() => !cancelled && setLists((all) => ({ ...all, [kind]: [] })));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, given, kind]);

    const switchKind = (next: VendorKind) => {
        setKind(next);
        setPicked(null);
    };

    const close = (next: boolean) => {
        if (!next) {
            setMode('view');
            setReason('');
            setPicked(null);
            setKind('customer');
            setLists({});
        }
        onOpenChange(next);
    };

    const start = async () => {
        if (busy || !vendor) return;
        setBusy(true);
        try {
            const session = await api.start({ vendor: vendor._id, mode, reason: reason.trim() || undefined });
            close(false);
            onStarted(session);
        } catch (error) {
            console.error('Error starting support session:', error);
            toast.error(t('SUPPORT_ACCESS.START_FAILED'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="sm:max-w-md" data-testid="open-as-vendor-dialog">
                <DialogHeader>
                    <DialogTitle>{given?.name ? t('SUPPORT_ACCESS.DIALOG_TITLE', { vendor: given.name }) : t('SUPPORT_ACCESS.OPEN')}</DialogTitle>
                    <DialogDescription>{t('SUPPORT_ACCESS.DIALOG_DESCRIPTION')}</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-5">
                    {!given && (
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="support-vendor" className="text-xs text-muted-foreground">{t('SUPPORT_ACCESS.VENDOR_LABEL')}</Label>
                            <PillTabs<VendorKind>
                                value={kind}
                                onChange={switchKind}
                                ariaLabel={t('SUPPORT_ACCESS.KIND_LABEL')}
                                className="mb-1 w-full [&>button]:flex-1 [&>button]:justify-center"
                                options={[
                                    { value: 'customer', label: t('SUPPORT_ACCESS.KIND_CUSTOMERS'), icon: <Building2 /> },
                                    { value: 'prospect', label: t('SUPPORT_ACCESS.KIND_PROSPECTS'), icon: <Sparkles /> },
                                ]}
                            />
                            <VendorCombobox
                                key={kind}
                                id="support-vendor"
                                vendors={vendors ?? []}
                                value={picked?._id || ''}
                                selected={picked as any}
                                onChange={(id) => setPicked((vendors ?? []).find((v) => v._id === id) || null)}
                                placeholder={t('SUPPORT_ACCESS.VENDOR_PLACEHOLDER')}
                                searchPlaceholder={t('SUPPORT_ACCESS.VENDOR_SEARCH')}
                                emptyText={t('SUPPORT_ACCESS.VENDOR_EMPTY')}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <span className="text-xs text-muted-foreground">{t('SUPPORT_ACCESS.MODE_LABEL')}</span>
                        <PillTabs<SupportMode>
                            value={mode}
                            onChange={setMode}
                            ariaLabel={t('SUPPORT_ACCESS.MODE_LABEL')}
                            className="w-full [&>button]:flex-1 [&>button]:justify-center"
                            options={[
                                { value: 'view', label: t('SUPPORT_ACCESS.MODE_VIEW'), icon: <Eye /> },
                                { value: 'edit', label: t('SUPPORT_ACCESS.MODE_EDIT'), icon: <Pencil /> },
                            ]}
                        />
                        <p className="text-xs text-muted-foreground" data-testid="support-mode-hint">
                            {t(mode === 'view' ? 'SUPPORT_ACCESS.MODE_VIEW_HINT' : 'SUPPORT_ACCESS.MODE_EDIT_HINT')}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="support-reason" className="text-xs text-muted-foreground">{t('SUPPORT_ACCESS.REASON_LABEL')}</Label>
                        <Input
                            id="support-reason"
                            value={reason}
                            maxLength={500}
                            placeholder={t('SUPPORT_ACCESS.REASON_PLACEHOLDER')}
                            onChange={(e) => setReason(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && start()}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => close(false)}>{t('ADMIN_SHELL.COMMON.CANCEL')}</Button>
                    <Button onClick={start} disabled={busy || !vendor}>
                        <UserRoundCog />
                        {t('SUPPORT_ACCESS.START')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
